/**
 * Empty state component - Reusable empty state UI
 */

import {ReactNode} from 'react';
import {Button} from './Button';
import {cn} from '../../utils/cn';

interface EmptyStateProps {
  /**
   * Icon or emoji to display
   */
  icon?: ReactNode;
  
  /**
   * Title text
   */
  title: string;
  
  /**
   * Description text
   */
  description?: string;
  
  /**
   * Primary action button
   */
  action?: {
    label: string;
    onClick: () => void;
    variant?: 'primary' | 'outline';
  };
  
  /**
   * Secondary action button
   */
  secondaryAction?: {
    label: string;
    onClick: () => void;
  };
  
  /**
   * Custom className
   */
  className?: string;
  
  /**
   * Size variant
   */
  size?: 'sm' | 'md' | 'lg';
}

export function EmptyState({
  icon,
  title,
  description,
  action,
  secondaryAction,
  className,
  size = 'md',
}: EmptyStateProps) {
  const sizeClasses = {
    sm: {
      icon: 'text-4xl mb-3',
      title: 'text-lg',
      description: 'text-sm',
    },
    md: {
      icon: 'text-5xl mb-4',
      title: 'text-xl',
      description: 'text-base',
    },
    lg: {
      icon: 'text-6xl mb-6',
      title: 'text-2xl',
      description: 'text-lg',
    },
  };

  const classes = sizeClasses[size];

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center py-12 px-4',
        className
      )}
    >
      {icon && (
        <div className={cn('mb-4', classes.icon)}>
          {icon}
        </div>
      )}
      
      <h3
        className={cn(
          'font-semibold text-gray-900 dark:text-white mb-2',
          classes.title
        )}
      >
        {title}
      </h3>
      
      {description && (
        <p
          className={cn(
            'text-gray-600 dark:text-gray-400 mb-6 max-w-md',
            classes.description
          )}
        >
          {description}
        </p>
      )}
      
      {(action || secondaryAction) && (
        <div className="flex flex-col sm:flex-row gap-3">
          {action && (
            <Button
              onClick={action.onClick}
              variant={action.variant || 'primary'}
            >
              {action.label}
            </Button>
          )}
          {secondaryAction && (
            <Button
              onClick={secondaryAction.onClick}
              variant="outline"
            >
              {secondaryAction.label}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}


